import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import NotFound from "./NotFound";
import { ArrowRight, Check } from "lucide-react";
import Seo from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import EnquiryForm from "../components/EnquiryForm";
import SectionHeader from "../components/SectionHeader";
import ExperienceCard from "../components/ExperienceCard";
import TastingCard from "../components/TastingCard";
import { experiences, tastings, tastingConditions } from "../data/site";
import { usePhotoOverrides } from "../lib/photos";

export default function ZazitekDetail() {
  const { slug } = useParams();
  const exp = experiences.find((e) => e.slug === slug);
  const photos = usePhotoOverrides();
  if (!exp) return <NotFound />;
  const galleryImages = [
    photos.first("experience", slug),
    ...[1, 2, 3, 4].map((n) => photos.first("experience-gallery", `${slug}-${n}`)),
  ].filter(Boolean).map((p) => ({ src: p.src, alt: p.alt || exp.title }));
  const related = experiences.filter((e) => e.slug !== slug).slice(0, 2);

  return (
    <main id="main">
      <Seo path={`/zazitky/${slug}`} title={`${exp.title} | Vinařství Brychta`} description={exp.short} image={exp.image || undefined} />
      <Breadcrumbs items={[{ label: "Zážitky", to: "/zazitky" }, { label: exp.title }]} />

      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-10 md:py-14 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <div className="text-[11px] tracking-[.28em] uppercase text-burgundy mb-4">Vinařský zážitek</div>
          <h1 className="font-serif font-light text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-charcoal">{exp.title}</h1>
          <p className="mt-6 text-lg text-charcoal/80 leading-relaxed max-w-2xl">{exp.intro}</p>
          <div className="mt-10 grid sm:grid-cols-3 gap-5">
            <Info label="Délka" value={exp.duration} />
            <Info label="Pro koho" value={exp.audience} />
            <Info label="Cena" value={exp.price} />
          </div>

          {exp.included?.length > 0 && (
            <div className="mt-10">
              <h2 className="font-serif text-2xl mb-4">Co je zahrnuto</h2>
              <ul className="space-y-2">
                {exp.included.map((it, i) => (
                  <li key={i} className="flex gap-2 text-charcoal/85"><Check size={18} className="text-gold mt-1 shrink-0" /> {it}</li>
                ))}
              </ul>
            </div>
          )}
          {exp.notes?.length > 0 && (
            <div className="mt-10">
              <h2 className="font-serif text-2xl mb-4">Praktické informace</h2>
              <ul className="space-y-2 text-charcoal/75">
                {exp.notes.map((n, i) => <li key={i}>• {n}</li>)}
              </ul>
            </div>
          )}
        </div>

        <aside className="lg:col-span-5">
          <div className="sticky top-24">
            <ExperienceGallery key={slug} images={galleryImages} />
            <a href="#poptavka" className="mt-4 block text-center bg-burgundy hover:bg-burgundy-hover text-ivory py-3.5 tracking-wide">Poptat tento zážitek</a>
          </div>
        </aside>
      </section>

      {exp.showTastingVariants && (
        <section className="bg-ivory pb-16 md:pb-24" data-testid="tasting-variants">
          <div className="max-w-[1400px] mx-auto px-5 md:px-10">
            <SectionHeader eyebrow="Varianty degustace" title="Vyberte si, kolik času chcete vínu věnovat" />
            <div className="mt-10 grid md:grid-cols-3 gap-5">
              {tastings.map((t) => <TastingCard key={t.slug} t={t} ctaHref="#poptavka" />)}
            </div>
            <div className="mt-8 max-w-3xl text-sm text-charcoal/75 leading-relaxed space-y-1.5">
              <p>{tastingConditions.minParticipants} {tastingConditions.smallGroups} {tastingConditions.bigGroups}</p>
              <p>{tastingConditions.vineyardSurcharge} {tastingConditions.privateCouple}</p>
              <p>{tastingConditions.board}</p>
            </div>
          </div>
        </section>
      )}

      <section id="poptavka" className="bg-cream py-16 md:py-24">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10">
          <SectionHeader eyebrow="Nezávazná poptávka" title="Ozveme se s konkrétní nabídkou" />
          <div className="mt-8 bg-ivory p-6 md:p-10 border border-charcoal/10">
            <EnquiryForm
              key={exp.slug}
              defaultType={exp.enquiryType}
              defaultExperience={exp.slug}
              defaultAdults={exp.prefillAdults != null ? String(exp.prefillAdults) : undefined}
              defaultChildren={exp.prefillChildren != null ? String(exp.prefillChildren) : undefined}
            />
          </div>
        </div>
      </section>

      <section className="bg-ivory py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <SectionHeader eyebrow="Mohlo by vás zajímat" title="Další zážitky" />
          <div className="mt-10 grid md:grid-cols-2 gap-5">
            {related.map((e) => {
              const ov = photos.first("experience", e.slug);
              return <ExperienceCard key={e.slug} exp={e} imageOverride={ov?.src} altOverride={ov?.alt} />;
            })}
          </div>
          <div className="mt-8"><Link to="/zazitky" className="inline-flex items-center gap-2 text-burgundy text-sm">Všechny zážitky <ArrowRight size={14} /></Link></div>
        </div>
      </section>
    </main>
  );
}

function ExperienceGallery({ images }) {
  const [active, setActive] = useState(0);
  const main = images[active] || images[0];
  return (
    <div data-testid="experience-gallery">
      <div className="w-full aspect-[4/5] bg-cream overflow-hidden">
        {main && <img src={main.src} alt={main.alt} className="w-full h-full object-cover" data-testid="experience-main-photo" />}
      </div>
      {images.length > 1 && (
        <div className="mt-3 grid grid-cols-5 gap-2">
          {images.map((im, i) => (
            <button key={im.src} type="button" onClick={() => setActive(i)} aria-label={`Zobrazit fotografii ${i + 1}`} aria-pressed={i === active}
              data-testid={`experience-thumb-${i}`}
              className={`aspect-square overflow-hidden border-2 transition-colors ${i === active ? "border-burgundy" : "border-transparent hover:border-charcoal/30"}`}>
              <img src={im.src} alt="" className="w-full h-full object-cover" loading="lazy" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="border-t border-charcoal/15 pt-4">
      <div className="text-[10px] tracking-[.22em] uppercase text-charcoal/50">{label}</div>
      <div className="font-serif text-lg text-charcoal mt-1">{value}</div>
    </div>
  );
}
